import { StandardEvents } from '@shopify/events';

/**
 * Bundle "1/2/3 tapis" - fiche produit Premium - CleanyStep uniquement.
 *
 * v2 (refonte visuelle complete - 3 cartes empilees, voir la doc en tete de
 * product-bundle-selector.liquid pour le detail de la demande et des choix
 * de construction). La logique d'etat/synchronisation ci-dessous reste
 * directement heritee de la v1 (pilules texte) - seule la structure DOM
 * qu'elle pilote a change (cartes + swatches carres au lieu de pilules).
 *
 * Etat 100% local (tier + couleur par tapis) jusqu'au clic "Ajouter au
 * panier" (voir cs-bundle-add-to-cart.js, qui lit getSelection() ci-dessous
 * au moment de la soumission - AUCUN changement cote de ce fichier, son API
 * publique - getSelection()/tierData - est restee identique). Rien ici
 * n'appelle /cart/add.js.
 *
 * Synchronisation galerie/prix/barre de stock : chaque changement de tier ou
 * de couleur d'un tapis coche, en silence, le vrai bouton couleur natif
 * correspondant (fieldset[data-fieldset-index="0"]) et declenche son
 * 'change' - la galerie (script dans invisible_style_blocks,
 * product.cleanystep.json) et stock-progress.js (natif) reagissent alors
 * exactement comme pour un vrai clic utilisateur sur le vrai selecteur
 * couleur, sans code duplique ici. window.__csGallerySuppressRebuild (deja
 * en place, voir assets/media-gallery.js) evite que ce changement synthetique
 * ne reconstruise la galerie - meme garde que le mecanisme existant.
 *
 * NOUVEAU en v2 : le vrai fieldset Couleur natif est desormais masque EN
 * PERMANENCE (avant : visible pour le palier 1 uniquement) - le choix de
 * couleur passe maintenant exclusivement par les swatches de ce bloc, y
 * compris a 1 seul tapis, pour un mecanisme unique et coherent sur les 3
 * paliers. Il reste synchronise en silence (voir ci-dessus) uniquement
 * comme mecanisme de declenchement galerie/stock, jamais pour etre affiche.
 *
 * Le prix natif tout en haut de la fiche (<product-price>) n'est plus jamais
 * masque (v3, demande explicite du marchand : il doit toujours refleter le
 * palier + la taille choisis dans le bundle) - son CONTENU est REMPLACE par
 * #updateAllPrices ci-dessous, toujours une chaine DEJA formatee cote Liquid
 * (filtre money) - aucun montant recalcule ici.
 *
 * BUG EVITE (verifie en reel avant de considerer la v3 finie - le prix
 * revenait silencieusement a la valeur native apres un delai variable,
 * pas a chaque fois, ce qui aurait ete tres difficile a repérer sans un
 * test cible) : #syncNativeColor ci-dessous coche le vrai bouton couleur et
 * declenche son 'change' - assets/variant-picker.js y reagit en lancant SA
 * PROPRE requete reseau asynchrone (fetchUpdatedSection), qui, une fois
 * resolue (StandardEvents.productSelect, cf. assets/product-price.js),
 * REMPLACE ENTIEREMENT le noeud [ref="priceContainer"] de <product-price>
 * par une version fraiche refletant le VRAI prix de la couleur choisie sur
 * le produit ORIGINAL - jamais celui du bundle. Un appel synchrone unique a
 * #updateAllPrices juste apres avoir declenche ce 'change' arrivait donc
 * AVANT cette reponse reseau, et se faisait ecraser par elle des qu'elle
 * arrivait. Corrige en ecoutant ce meme evenement (meme mecanique que
 * cart-loading-overlay.js/cart-line-tier-swap.js pour les evenements
 * panier) et en ne reappliquant #updateAllPrices qu'UNE FOIS SA PROMESSE
 * RESOLUE - toujours le dernier mot, quel que soit le delai reseau reel,
 * plutot qu'un delai fixe devine a l'aveugle.
 *
 * BUG EVITE (trouve en verifiant le DOM reel avant de considerer la v2
 * finie, jamais publie) : chaque carte rendait au debut sa PROPRE copie des
 * rangees "Tapis N" (voir product-bundle-selector.liquid). Deux radios de
 * "meme tapis" mais de cartes differentes partageaient alors le meme
 * attribut name="cs-bundle-tapis-N-color" sur des noeuds DOM distincts - les
 * navigateurs regroupent les radios par ce seul attribut, ou qu'ils soient
 * dans le DOM, donc cocher une copie decochait silencieusement l'autre,
 * meme cachee dans une autre carte. #placeTapisRows ci-dessous fonctionne
 * desormais sur un DOM corrige en amont : chaque tapis n'existe plus
 * qu'UNE SEULE fois (dans .cs-bundle__tapis-rows-source), deplacee (jamais
 * copiee) dans le slot de la carte active a chaque changement de palier.
 */
class CsBundleSelector extends HTMLElement {
  connectedCallback() {
    /** @type {{color: string, size: string, available: boolean}[]} */
    this.variants = this.#parseJsonAttr('variants') || [];
    /** @type {{[tier: string]: {[size: string]: {variantId?: string, priceFormatted: string, referencePriceFormatted?: string, savingsPercent?: number}}}} */
    this.tierData = this.#parseJsonAttr('tier-data') || {};

    /** @type {1|2|3} */
    this.tier = 1;
    /** @type {{1: string, 2: string, 3: string}} */
    this.tapisColors = {
      1: this.dataset.defaultColor,
      2: this.dataset.defaultColor,
      3: this.dataset.defaultColor,
    };

    this.tierInputs = Array.from(this.querySelectorAll('[data-cs-tier-input]'));
    this.tapisColorInputs = Array.from(this.querySelectorAll('[data-cs-tapis-color-input]'));
    this.cardExpandedEls = Array.from(this.querySelectorAll('[data-cs-card-expanded]'));
    this.sizeIndicatorEls = Array.from(this.querySelectorAll('[data-cs-size-indicator]'));

    // Une seule instance par tapis (cf. doc de classe) - indexee par son
    // data-cs-tapis pour un acces direct dans #placeTapisRows.
    /** @type {{[tapisIndex: string]: HTMLElement}} */
    this.tapisRowsByIndex = {};
    this.querySelectorAll('.cs-bundle__tapis-rows-source > [data-cs-tapis]').forEach((row) => {
      if (row instanceof HTMLElement) this.tapisRowsByIndex[row.dataset.csTapis] = row;
    });
    /** @type {{[tier: string]: HTMLElement}} */
    this.tapisRowsSlots = {};
    this.querySelectorAll('[data-cs-tapis-rows-slot]').forEach((slot) => {
      if (slot instanceof HTMLElement) this.tapisRowsSlots[slot.dataset.csTapisRowsSlot] = slot;
    });

    this.tierInputs.forEach((input) => {
      input.addEventListener('change', () => this.#handleTierChange(Number(input.value)));
    });

    this.tapisColorInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const tapisIndex = Number(input.dataset.csTapis);
        this.tapisColors[tapisIndex] = input.value;
        this.#syncNativeColor(input.value);
      });
    });

    // Le vrai selecteur Couleur natif ne sert plus qu'a declencher, en
    // silence, la galerie/la barre de stock (cf. doc de classe) - masque des
    // le chargement, quel que soit le palier, et plus jamais reaffiche.
    const realColorFieldset = document.querySelector('variant-picker fieldset[data-fieldset-index="0"]');
    const colorLabel = document.querySelector('.cs-color-label');
    if (realColorFieldset instanceof HTMLElement) realColorFieldset.hidden = true;
    if (colorLabel instanceof HTMLElement) colorLabel.hidden = true;

    // Place les rangees "Tapis N" dans la carte 1 tapis (etat initial serveur)
    // avant meme tout changement de palier.
    this.#placeTapisRows(1);

    // Valeur de depart correcte des 3 cartes avant meme que le vrai fieldset
    // Taille soit retrouve ci-dessous (#currentSize() a son propre repli sur
    // data-default-size) - re-appelee une 2e fois juste en dessous une fois
    // ce fieldset confirme, sans effet visible si la taille n'a pas change.
    this.#updateAllPrices();

    this.#waitForVariantPicker((sizeFieldset) => {
      this.sizeFieldset = sizeFieldset;
      sizeFieldset.addEventListener('change', () => {
        this.#refreshAvailability();
        this.#updateAllPrices();
      });
      this.#refreshAvailability();
      this.#updateAllPrices();
    });

    // Voir "BUG EVITE" dans la doc de classe : reapplique le prix natif une
    // fois - et seulement une fois - que la requete reseau native
    // (declenchee par #syncNativeColor) a reellement fini de remplacer le
    // conteneur de <product-price>, quel que soit le delai reseau reel.
    document.addEventListener(StandardEvents.productSelect, (event) => {
      event.promise?.then(() => this.#updateAllPrices()).catch(() => {});
    });
  }

  /**
   * Deplace (jamais ne copie - voir doc de classe) les rangees "Tapis 1..N"
   * dans le slot de la carte du palier N, dans l'ordre. Element.appendChild
   * sur un noeud deja present ailleurs dans le DOM le DEPLACE (ne le clone
   * pas) - aucun etat (radio coche, listener) n'est perdu, meme sur un
   * "deplacement" vers le meme parent (no-op silencieux dans ce cas).
   * @param {number} tier
   */
  #placeTapisRows(tier) {
    const slot = this.tapisRowsSlots[String(tier)];
    if (!slot) return;
    for (let i = 1; i <= tier; i++) {
      const row = this.tapisRowsByIndex[String(i)];
      if (row) slot.appendChild(row);
    }
    // Le label "Tapis N" n'a de sens que s'il y a plusieurs rangees
    // simultanement visibles (palier >= 2) - a 1 seul tapis, redondant avec
    // le label "Couleur" juste au-dessus.
    Object.values(this.tapisRowsByIndex).forEach((row) => {
      const label = row.querySelector('.cs-bundle__tapis-row-label');
      if (label instanceof HTMLElement) label.hidden = tier === 1;
    });
  }

  /**
   * @param {string} name
   * @returns {any}
   */
  #parseJsonAttr(name) {
    const raw = this.dataset[name.replace(/-([a-z])/g, (_, c) => c.toUpperCase())];
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('[cs-bundle-selector] JSON attribute parse failed:', name, error);
      return null;
    }
  }

  /**
   * The real Taille fieldset isn't guaranteed to be upgraded/present the
   * instant this element's own connectedCallback runs (script execution
   * order across separate custom elements on the same page isn't
   * guaranteed) - short bounded retry, same spirit as other polling already
   * used in this product's custom_liquid script.
   * @param {(fieldset: Element) => void} callback
   */
  #waitForVariantPicker(callback) {
    const tryFind = () => document.querySelector('variant-picker fieldset[data-fieldset-index="1"]');
    const existing = tryFind();
    if (existing) {
      callback(existing);
      return;
    }
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const fieldset = tryFind();
      if (fieldset) {
        clearInterval(interval);
        callback(fieldset);
      } else if (attempts >= 50) {
        clearInterval(interval);
      }
    }, 100);
  }

  /** @returns {string} The currently selected real size value. */
  #currentSize() {
    const checked = this.sizeFieldset?.querySelector('input:checked');
    return checked instanceof HTMLInputElement ? checked.value : this.dataset.defaultSize;
  }

  /**
   * @param {number} tier
   */
  #handleTierChange(tier) {
    const previousTier = this.tier;
    this.tier = tier;

    if (tier > previousTier) {
      // Grandit : les tapis deja presents gardent leur couleur, les nouveaux
      // reprennent par defaut celle du Tapis 1 (meme regle que dans le
      // panier, appliquee ici aussi pour la coherence).
      for (let i = previousTier + 1; i <= tier; i++) {
        this.tapisColors[i] = this.tapisColors[1];
        const input = this.tapisColorInputs.find(
          (el) => Number(el.dataset.csTapis) === i && el.value === this.tapisColors[1]
        );
        if (input instanceof HTMLInputElement) input.checked = true;
      }
    }

    this.cardExpandedEls.forEach((el) => {
      const cardTier = Number(el.dataset.csCardExpanded);
      el.hidden = cardTier !== tier;
    });
    this.#placeTapisRows(tier);

    // La galerie/stock doivent refleter la derniere couleur pertinente -
    // celle du dernier tapis nouvellement visible si on vient d'agrandir,
    // sinon celle du Tapis 1.
    this.#syncNativeColor(this.tapisColors[tier > previousTier ? tier : 1]);

    this.#refreshAvailability();

    // BUG TROUVE ET CORRIGE (verifie en reel - le prix natif tout en haut de
    // la fiche ne suivait pas un changement de palier seul, seulement un
    // changement de taille) : #updateAllPrices() lit this.tier (deja mis a
    // jour juste au-dessus) pour ce prix natif - jusqu'ici jamais rappelee
    // depuis cette methode, uniquement depuis la connexion initiale et les
    // changements de TAILLE, ce qui suffisait avant (le prix des 3 cartes,
    // lui, ne depend pas du palier actuellement choisi - il affiche les 3
    // prix en permanence, quel que soit ce qui est selectionne).
    this.#updateAllPrices();
  }

  /**
   * Coche le vrai bouton couleur natif correspondant et declenche son
   * 'change' - reutilise tel quel le mecanisme galerie/stock deja stabilise.
   * Sans effet silencieux si la couleur n'existe pas parmi les vraies
   * options (ne devrait jamais arriver, les valeurs viennent de la meme
   * liste Liquid).
   * @param {string} colorValue
   */
  #syncNativeColor(colorValue) {
    const fieldset = document.querySelector('variant-picker fieldset[data-fieldset-index="0"]');
    if (!fieldset) return;
    const input = fieldset.querySelector(`input[value="${CSS.escape(colorValue)}"]`);
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    window.__csGallerySuppressRebuild = true;
    setTimeout(() => {
      window.__csGallerySuppressRebuild = false;
    }, 0);
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Marque les swatches indisponibles pour la taille actuellement choisie,
   * dans toutes les rangees "Tapis N" visibles - meme matrice couleur x
   * taille que le vrai produit (donnee reelle, lue une fois au chargement).
   * Le CSS consommant data-cs-unavailable (product-bundle-selector.liquid)
   * les masque entierement (v4, demande explicite du marchand) - inchange
   * ici, cette methode ne fait que calculer/exposer la disponibilite reelle.
   */
  #refreshAvailability() {
    const size = this.#currentSize();
    this.tapisColorInputs.forEach((input) => {
      const entry = this.variants.find((v) => v.color === input.value && v.size === size);
      const available = entry ? entry.available : true;
      input.toggleAttribute('data-cs-unavailable', !available);
      // Ne desactive pas le radio lui-meme (choix delibere) : un client peut
      // deja avoir cette couleur cochee pour ce tapis avant de changer de
      // taille vers une combinaison devenue indisponible - son swatch
      // disparait alors visuellement (v4) mais la selection reste valide
      // en interne (getSelection()), sans casser le formulaire.
    });
  }

  /**
   * Met a jour le prix affiche des 3 cartes, ainsi que le texte d'economie +
   * le prix de reference barre - systematique sur les 3 paliers. Toujours
   * des chaines DEJA formatees par Liquid (filtre money) ou un pourcentage
   * DEJA calcule cote Liquid a partir des vrais prix. Independant du palier
   * actuellement selectionne : les 3 cartes restent a jour meme collapsees,
   * puisqu'on peut les cliquer n'importe laquelle a tout moment.
   *
   * Met aussi a jour <product-price> (natif, tout en haut de la fiche) -
   * v2 (demande explicite du marchand) : ce prix doit TOUJOURS refleter le
   * palier + la taille actuellement choisis dans le bundle, plutot que
   * d'etre simplement masque des le palier 2/3 (comportement v1). Son
   * contenu est directement REMPLACE (pas seulement son style) par les
   * memes chaines deja formatees que les cartes, structure HTML copiee du
   * rendu natif (memes classes/libelles d'accessibilite) pour rester
   * coherent avec le reste du theme si jamais il est restyle plus tard.
   */
  #updateAllPrices() {
    const size = this.#currentSize();

    for (let tier = 1; tier <= 3; tier++) {
      const entry = this.tierData[String(tier)]?.[size];
      if (!entry) continue;

      const priceEl = this.querySelector(`[data-cs-card-price="${tier}"]`);
      if (priceEl) priceEl.textContent = entry.priceFormatted;

      const savingsEl = this.querySelector(`[data-cs-card-savings="${tier}"]`);
      const referenceEl = this.querySelector(`[data-cs-card-reference="${tier}"]`);
      if (savingsEl) savingsEl.textContent = typeof entry.savingsPercent === 'number' ? `Tu économises ${entry.savingsPercent}%` : '';
      if (referenceEl) referenceEl.textContent = entry.referencePriceFormatted || '';
    }

    // BUG TROUVE ET CORRIGE (v1, garde ici pour memoire) :
    // <product-price>.hidden=true seul ne masquait rien a l'ecran - le
    // theme lui pose deja sa propre regle CSS "display", qui gagne sur le
    // display:none par defaut de l'attribut hidden. Plus utilise en v2 (le
    // prix natif n'est jamais masque, il est remplace), mais la meme
    // lecon s'applique si ce prix devait un jour etre re-masque.
    const nativePrice = document.querySelector('product-price');
    const currentEntry = this.tierData[String(this.tier)]?.[size];
    if (nativePrice instanceof HTMLElement && currentEntry) {
      nativePrice.style.removeProperty('display');
      const container = nativePrice.querySelector('[ref="priceContainer"]') || nativePrice;
      const referenceHtml = currentEntry.referencePriceFormatted
        ? `<span class="price-item__group price-item--regular compare-at-price"><span class="visually-hidden">Prix régulier&nbsp;</span>${currentEntry.referencePriceFormatted}</span>`
        : '';
      const saleLabel = currentEntry.referencePriceFormatted ? 'Prix promotionnel' : 'Prix';
      // Badge "-X%" remis (demande explicite du marchand - avait ete
      // entierement supprime plus tot dans ce projet) juste a cote du prix,
      // DANS le meme conteneur remplace ci-dessus plutot qu'un element a
      // part : reflete desormais le palier ACTUELLEMENT selectionne
      // (savingsPercent, deja calcule cote Liquid a partir des vrais prix -
      // tier_data_json plus bas dans ce fichier - jamais recalcule ici),
      // contrairement a l'ancien badge qui affichait toujours "-20%" en dur
      // quel que soit le palier.
      const badgeHtml = typeof currentEntry.savingsPercent === 'number'
        ? `<span class="cs-price-badge">-${currentEntry.savingsPercent}%</span>`
        : '';
      container.innerHTML = `<span class="price-item__group price"><span class="visually-hidden">${saleLabel}&nbsp;</span>${currentEntry.priceFormatted}</span>${referenceHtml}${badgeHtml}`;
    }

    this.sizeIndicatorEls.forEach((el) => {
      el.textContent = `Taille sélectionnée : ${size}`;
    });
  }

  /**
   * Lu par cs-bundle-add-to-cart.js au moment de la soumission du
   * formulaire - seule surface publique de cet element, inchangee depuis la
   * v1 (voir doc de classe).
   * @returns {{tier: 1|2|3, size: string, colors: string[]}}
   */
  getSelection() {
    const colors = [];
    for (let i = 1; i <= this.tier; i++) colors.push(this.tapisColors[i]);
    return { tier: this.tier, size: this.#currentSize(), colors };
  }
}

if (!customElements.get('cs-bundle-selector')) {
  customElements.define('cs-bundle-selector', CsBundleSelector);
}
